// @ts-strict-ignore
import CommandPalette, { filterItems, getItemIndex, JsonStructure } from 'react-cmdk';
import { useState, useEffect } from 'react';
import * as React from 'react';
import 'react-cmdk/dist/cmdk.css';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
    createAppReleaseCalendarRoute,
    createAppRoute,
    createAppSettingsRoute,
    createOrgSettingsRoute,
    createReleaseOverviewRoute,
    createReleaseRolloutRoute,
    createReleaseRoute,
    createReleaseStepRoute,
    createSettingsMenuRoute,
} from '@/helpers/routeHelpers';
import { AppRouteComponentMatchParams, OrgRouteComponentMatchParams, OrgSettingsTabType } from '@/typescriptHelpers';
import routes from '@/routes';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectApp,
    selectIsTriageExcludedForOrg,
    selectIsUserflowOnboardingEnabledForOrg,
    selectUserIsRunwayAdmin,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { getAllAppsInOrg, getIdFromAppId, getIdFromOrgId } from '@/helpers';
import Avatar from 'antd/lib/avatar';
import { Release, ReleaseStatus, ReleaseType } from '@/types';
import Icon, {
    AppstoreFilled,
    CalendarFilled,
    FieldNumberOutlined,
    FireOutlined,
    HomeFilled,
    RollbackOutlined,
    SettingFilled,
    SignalFilled,
} from '@ant-design/icons';
import { settingsMenuItems } from '../appSettings/appSettingsHelpers';
import { antGrey } from '../styles/globalStyles';
import { selectCompletedReleases, selectCurrentReleases, selectUpcomingReleases } from '@/selectors/memoizedSelectors';
import { trackAppRouteEvent, trackOrgRouteEvent } from '@/actions/trackEvent';
import { appNameWithPlatform, platformLabelForAppName } from '@/helpers/mdmPlatformOverride';
import * as Analytics from '@/utils/analytics';

const AppRouter = {
    OrgSettings: createOrgSettingsRoute,
    AppHome: createAppRoute,
    AppReleaseSchedule: createAppReleaseCalendarRoute,
    AppSettings: createAppSettingsRoute,
    Release: createReleaseRoute,
    ReleaseStep: createReleaseStepRoute,
    Rollout: createReleaseRolloutRoute,
    ReleaseOverview: createReleaseOverviewRoute,
};

enum CommandPage {
    Root = 'root',
    Releases = 'releases',
}

const CommandModal: React.FC = () => {
    const orgRouteMatch = useRouteMatch<OrgRouteComponentMatchParams>(routes.org);
    const appRouteMatch = useRouteMatch<AppRouteComponentMatchParams>(routes.app);

    const orgIdParam = orgRouteMatch?.params.orgIdParam;
    const appIdParam = appRouteMatch?.params.appIdParam;

    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state);
        }

        return null;
    });

    const appId = useSelector((state: RootState) => {
        if (orgIdParam && appIdParam) {
            return getIdFromAppId(orgIdParam, appIdParam, state);
        }

        return null;
    });

    const apps = useSelector((state: RootState) => (orgId ? getAllAppsInOrg(orgId, state) : []));
    const upcomingReleases = useSelector((state: RootState) => (appId ? selectUpcomingReleases(state, appId) : []));
    const nextReleases = useSelector((state: RootState) => (appId ? selectCurrentReleases(state, appId) : []));
    const compeletedReleases = useSelector((state: RootState) => (appId ? selectCompletedReleases(state, appId) : []));

    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const isTriageExcluded = useSelector(selectIsTriageExcludedForOrg);
    const isUserflowOnboardingEnabled = useSelector((state: RootState) =>
        orgId ? selectIsUserflowOnboardingEnabledForOrg(state, orgId) : false,
    );
    const currentApp = useSelector((state: RootState) => (appId ? selectApp(state, appId) : null));

    const nextReleaseForApp = useSelector((state: RootState) => {
        if (appId) {
            return selectCurrentReleases(state, appId).find((release) => release.type !== ReleaseType.Rollback);
        }

        return null;
    });

    const liveReleaseIdForApp = useSelector((state: RootState) => {
        if (appId) {
            const completedReleases = selectCompletedReleases(state, appId);

            // if completed releases have previously been loaded, use the latest of those
            if (completedReleases.length > 0) {
                return completedReleases[0].releaseId;
            }

            // otherwise, take the liveReleaseId last reported by the BE on AppEntity
            return selectApp(state, appId).liveReleaseId;
        }

        return null;
    });

    const [page, setPage] = useState<CommandPage>(CommandPage.Root);
    const [open, setOpen] = useState<boolean>(false);
    const [search, setSearch] = useState('');
    const history = useHistory();
    const dispatch = useDispatch();

    const appSettingsMenuItems = settingsMenuItems.filter((item) => {
        if (item.isRunwayAdminRouteOnly && !isRunwayAdmin) return false;
        if (item.showOnlyForWhiteLabelApps && !currentApp?.appSettings?.isWhiteLabel) return false;
        if (item.hideForTriageExcludedOrgs && isTriageExcluded) return false;
        if (item.isReplacedByUserflowOnboarding && isUserflowOnboardingEnabled) return false;
        return true;
    });

    const goToRouteAndTrack = (route: string, pageName: string) => {
        history.push(route);
        if (orgId) {
            const baseProps = {
                orgId,
                eventName: Analytics.Event.NavigateViaCommandPalette,
                page: pageName,
            };

            if (appId) {
                dispatch(
                    trackAppRouteEvent({
                        ...baseProps,
                        appId,
                    }),
                );
            } else {
                dispatch(trackOrgRouteEvent(baseProps));
            }
        }
    };

    const appsGroup = {
        heading: 'Apps',
        id: 'apps',

        items: apps.map((app) => {
            const appName = app.appSettings.appName;
            return {
                id: app.id,
                children: appNameWithPlatform(appName, platformLabelForAppName(app.appSettings)),
                keywords: [app.appId],
                showType: false,
                icon: () => (
                    <Avatar size={18} src={app.appSettings.appImageUrl}>
                        {appName.charAt(0)}
                    </Avatar>
                ),
                onClick: () => {
                    goToRouteAndTrack(AppRouter.AppHome(orgIdParam, app.appId), appName);
                },
            };
        }),
    };

    const createReleaseListItem = (release: Release) => {
        const version = release.displayableVersion;
        const isHotfix = release.type === ReleaseType.Hotfix;
        const isRollback = release.type === ReleaseType.Rollback;
        return {
            id: release.id,
            children: `${version}${release.status === ReleaseStatus.Skipped ? ' (skipped)' : ''}`,
            showType: false,
            icon: () => {
                if (isHotfix) {
                    return <FireOutlined style={{ color: antGrey.grey7 }} />;
                } else if (isRollback) {
                    return <RollbackOutlined style={{ color: antGrey.grey7 }} />;
                } else {
                    return <FieldNumberOutlined style={{ color: antGrey.grey7 }} />;
                }
            },
            onClick: () => {
                goToRouteAndTrack(AppRouter.Release(orgIdParam, appIdParam, release.releaseId), version);
            },
        };
    };

    const releaseGroups: JsonStructure = [
        {
            heading: 'Upcoming',
            id: 'upcoming-releases',
            items: upcomingReleases.map(createReleaseListItem),
        },
        {
            heading: 'Next',
            id: 'next-releases',
            items: nextReleases.map(createReleaseListItem),
        },
        {
            heading: 'Completed',
            id: 'completed-releases',
            items: compeletedReleases.map(createReleaseListItem),
        },
    ];

    const filteredReleasesItems = filterItems(releaseGroups, search);

    const appRouteItems: JsonStructure = [
        {
            heading: 'Go to',
            id: 'go-to-app-page',
            items: [
                {
                    id: 'home',
                    children: 'App overview',
                    icon: () => <HomeFilled style={{ color: antGrey.grey7 }} />,
                    showType: false,
                    onClick: () => {
                        goToRouteAndTrack(AppRouter.AppHome(orgIdParam, appIdParam), 'App overview');
                    },
                },
                {
                    id: 'app-releases-schedule',
                    children: 'Release schedule',
                    icon: () => <CalendarFilled style={{ color: antGrey.grey7 }} />,
                    showType: false,
                    onClick: () => {
                        goToRouteAndTrack(AppRouter.AppReleaseSchedule(orgIdParam, appIdParam), 'Release schedule');
                    },
                },
                {
                    id: 'next-release',
                    children: 'Next release overview',
                    icon: () => <FieldNumberOutlined style={{ color: antGrey.grey7 }} />,
                    showType: false,
                    disabled: !nextReleaseForApp,
                    onClick: () => {
                        if (nextReleaseForApp) {
                            goToRouteAndTrack(
                                AppRouter.ReleaseOverview(orgIdParam, appIdParam, nextReleaseForApp.releaseId),
                                'Next release overview',
                            );
                        }
                    },
                },
                {
                    id: 'live-release',
                    children: 'Live release rollout',
                    icon: () => <SignalFilled style={{ color: antGrey.grey7 }} />,
                    showType: false,
                    disabled: !liveReleaseIdForApp,
                    onClick: () => {
                        if (liveReleaseIdForApp) {
                            goToRouteAndTrack(
                                AppRouter.Rollout(orgIdParam, appIdParam, liveReleaseIdForApp),
                                'Live release rollout',
                            );
                        }
                    },
                },
                {
                    id: 'go-to-releases',
                    children: 'All releases',
                    icon: () => <AppstoreFilled style={{ color: antGrey.grey7 }} />,
                    showType: false,
                    closeOnSelect: false,
                    onClick: () => {
                        setPage(CommandPage.Releases);
                    },
                },
            ],
        },
        {
            heading: 'App settings',
            id: 'go-to-app-settings',
            items: appSettingsMenuItems.map((menuItem) => {
                return {
                    id: menuItem.id,
                    children: menuItem.label,
                    icon: () => <Icon component={menuItem.icon} style={{ color: antGrey.grey7 }}></Icon>,
                    showType: false,
                    onClick: () => {
                        const route = createSettingsMenuRoute(orgId, appId, menuItem.path);
                        goToRouteAndTrack(route, menuItem.label);
                    },
                };
            }),
        },
    ];

    const conditionalAppItems: JsonStructure = appRouteMatch ? appRouteItems : [];

    const conditionalReleaseItems: JsonStructure = search && appRouteMatch ? releaseGroups : [];

    const filteredOrgItems = filterItems(
        [
            ...conditionalAppItems,
            ...conditionalReleaseItems,
            appsGroup,
            {
                heading: 'Organization',
                id: 'go-to-org-settings',
                items: [
                    {
                        id: 'org-settings',
                        children: 'Org settings',
                        icon: () => <SettingFilled style={{ color: antGrey.grey7 }} />,
                        showType: false,
                        onClick: () => {
                            goToRouteAndTrack(
                                AppRouter.OrgSettings(orgIdParam, OrgSettingsTabType.Team),
                                'Org settings',
                            );
                        },
                    },
                ],
            },
        ],
        search,
        {
            filterOnListHeading: true,
        },
    );

    useEffect(() => {
        const down = (e) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    useEffect(() => {
        if (!open) return;

        if (orgId && appId) {
            dispatch(
                trackAppRouteEvent({
                    orgId,
                    appId,
                    eventName: Analytics.Event.OpenCommandPalette,
                }),
            );
        } else if (orgId) {
            dispatch(
                trackOrgRouteEvent({
                    orgId,
                    eventName: Analytics.Event.OpenCommandPalette,
                }),
            );
        }
    }, [open]);

    return (
        <CommandPalette onChangeSearch={setSearch} onChangeOpen={setOpen} search={search} isOpen={open} page={page}>
            <CommandPalette.Page id={CommandPage.Root}>
                {filteredOrgItems.length ?
                    filteredOrgItems.map((list) => (
                        <CommandPalette.List key={list.id} heading={list.heading}>
                            {list.items.map(({ id, keywords: _keywords, ...rest }) => (
                                <CommandPalette.ListItem
                                    key={id}
                                    index={getItemIndex(filteredOrgItems, id)}
                                    {...rest}
                                />
                            ))}
                        </CommandPalette.List>
                    ))
                :   <CommandPalette.FreeSearchAction />}
            </CommandPalette.Page>
            <CommandPalette.Page
                id={CommandPage.Releases}
                searchPrefix={['Releases']}
                onEscape={() => setPage(CommandPage.Root)}
            >
                {filteredReleasesItems.length ?
                    filteredReleasesItems.map((list) => (
                        <CommandPalette.List key={list.id} heading={list.heading}>
                            {list.items.map(({ id, ...rest }) => (
                                <CommandPalette.ListItem
                                    key={id}
                                    index={getItemIndex(filteredReleasesItems, id)}
                                    {...rest}
                                />
                            ))}
                        </CommandPalette.List>
                    ))
                :   <CommandPalette.FreeSearchAction />}
            </CommandPalette.Page>
        </CommandPalette>
    );
};

export default CommandModal;
